import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import {assignIfDeepDifferent} from '../../../../../reducers/utils'
import type {PipelineId} from '../../../../../types'
import {truthy} from '../../../../../utils/guards'
import type {NewVcsRootDto} from '../../services/pipelinesApi.types'
import type {JobRepositorySettings} from '../../types'
import {getVcsRootReference} from '../EditPipelinePage.utils'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getJob,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

const mainRepositoryName = 'main'

export const updateMainVcsRoot: CaseReducer<
  PipelineDraftState,
  // `pipelineBranch` is the store partition key; the spread `NewVcsRootDto` carries
  // its own `branch` (the VCS git branch), so the two must not share a key.
  PayloadAction<Partial<NewVcsRootDto> & {pipelineId: PipelineId; pipelineBranch: string}>
> = (state, action) => {
  const {pipelineId, pipelineBranch, ...updateVcsRoot} = action.payload
  const {draft} = getPipelineDraftEntry(state, pipelineId, pipelineBranch) ?? {}

  if (draft != null) {
    if (draft.vcsRoot != null) {
      draft.vcsRoot = {
        ...draft.vcsRoot,
        ...updateVcsRoot,
      }
    } else if (updateVcsRoot.url != null) {
      draft.vcsRoot = updateVcsRoot as NewVcsRootDto
    }
  }
}

export const setAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<NewVcsRootDto & {pipelineId: PipelineId; pipelineBranch: string}>
> = (state, action) => {
  const {pipelineId, pipelineBranch, ...vcsRoot} = action.payload
  const {draft} = getPipelineDraftEntry(state, pipelineId, pipelineBranch) ?? {}
  if (draft != null) {
    draft.additionalVcsRoots ??= []
    draft.additionalVcsRoots?.push(vcsRoot)
  }
}

export const deleteAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; reference: string; branch: string}>
> = (state, action) => {
  const {id, reference, branch} = action.payload
  const pipeline = ensurePipelineDraftEntry(state, id, branch)
  const pipelineJobs = pipeline.draft?.settings.jobs ?? {}

  pipeline.deleted ??= {}
  pipeline.deleted!.additionalVcsRoots ??= []
  pipeline.deleted!.additionalVcsRoots!.push(reference)

  // Set enabled the main repository if not one remaining
  Object.entries(pipelineJobs).forEach(([_, value]) => {
    const jobRepositories = value.repositories ?? []
    const repositoriesKeysSet = new Set(
      jobRepositories.flatMap(repository => Object.keys(repository)),
    )
    const hasReferencedRepositories = repositoriesKeysSet.has(reference)
    const isMainRepositoryDisabled = repositoriesKeysSet.has(mainRepositoryName)
    const isMainEnabled =
      hasReferencedRepositories && isMainRepositoryDisabled && repositoriesKeysSet.size === 2

    if (isMainEnabled) {
      value.repositories = value.repositories?.filter(
        repository => !(mainRepositoryName in repository),
      )
    }
  })
}

export const deleteIncompleteAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; index: number; branch: string}>
> = (state, action) => {
  const {id, index, branch} = action.payload
  const pipeline = ensurePipelineDraftEntry(state, id, branch)
  const additionalVcsRoots = pipeline.draft?.additionalVcsRoots

  pipeline.deleted ??= {}
  pipeline.deleted!.incompleteAdditionalVcsRoots ??= []
  pipeline.deleted!.incompleteAdditionalVcsRoots!.push(index)

  if (additionalVcsRoots?.length != null) {
    pipeline.draft!.additionalVcsRoots = additionalVcsRoots.filter(
      (_, itemIndex) => itemIndex !== index,
    )
  }
}

export const restoreAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<{id: string; reference: string; branch: string}>
> = (state, action) => {
  const {id, reference, branch} = action.payload
  const {deleted} = getPipelineDraftEntry(state, id, branch) ?? {}

  if (deleted?.additionalVcsRoots?.includes(reference)) {
    deleted.additionalVcsRoots = deleted.additionalVcsRoots.filter(
      deletedReference => deletedReference !== reference,
    )
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    deleteIfEmpty(deleted, 'additionalVcsRoots') &&
      deleteIfEmpty(getPipelineDraftEntry(state, id, branch), 'deleted')
  }
}

export const updateAdditionalVcsRoot: CaseReducer<
  PipelineDraftState,
  PayloadAction<
    Partial<NewVcsRootDto> & {pipelineId: PipelineId; index: number; pipelineBranch: string}
  >
> = (state, action) => {
  const {pipelineId, index, pipelineBranch, ...updateVcsRoot} = action.payload
  const {draft} = getPipelineDraftEntry(state, pipelineId, pipelineBranch) ?? {}

  if (draft != null) {
    const additionalVcsRoot = draft?.additionalVcsRoots[index]
    const jobs = draft?.settings.jobs
    const prevReference = getVcsRootReference(additionalVcsRoot)
    const updatedReference = getVcsRootReference({
      ...additionalVcsRoot,
      ...updateVcsRoot,
    })
    const prevUrl = additionalVcsRoot?.url
    const updatedUrl = updateVcsRoot.url ?? additionalVcsRoot?.url

    const referencesToReplace = new Set(
      [prevReference, prevUrl, updatedUrl]
        .filter(truthy)
        .filter(value => value !== updatedReference),
    )

    if (updatedReference != null && referencesToReplace.size > 0) {
      Object.keys(jobs ?? []).forEach(key => {
        const job = jobs![key]
        const repositories = job.repositories
        const jobRepository = repositories?.find(repository => {
          const [reference] = Object.keys(repository)
          return reference != null && referencesToReplace.has(reference)
        })

        if (jobRepository && repositories) {
          const [reference] = Object.keys(jobRepository)
          const hasUpdatedReference = repositories.some(
            repository => updatedReference in repository,
          )
          if (!hasUpdatedReference) {
            job.repositories?.push({
              [updatedReference]: jobRepository[reference],
            })
          }

          job.repositories = repositories?.filter(repository => {
            const [referenceKey] = Object.keys(repository)
            return referenceKey != null && !referencesToReplace.has(referenceKey)
          })
        }
      })
    }
    assignIfDeepDifferent(additionalVcsRoot, updateVcsRoot)
  }
}

export const updateJobRepository: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    url: string
    settings: JobRepositorySettings
    branch: string
  }>
> = (state, action) => {
  const {pipelineId, settings, jobId, url, branch} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (job != null) {
    const isMainRepository = url === mainRepositoryName
    const indexRepository = job.repositories?.findIndex(repository => url in repository)
    const hasRepository = indexRepository !== undefined && indexRepository !== -1

    job.repositories ??= []

    if (isMainRepository && hasRepository) {
      if (settings.enabled && !settings.path && !settings['checkout-rules']) {
        job.repositories = job.repositories?.filter(jobRepository => !(url in jobRepository))
      } else {
        job.repositories![indexRepository] = {
          [url]: settings,
        }
      }
    } else if (isMainRepository && !hasRepository) {
      job.repositories?.push({
        [url]: settings,
      })
    } else if (!isMainRepository && hasRepository) {
      job.repositories = job.repositories?.reduce<Record<string, JobRepositorySettings>[]>(
        (accumulator, repository) => {
          if (url in repository) {
            repository[url] = settings

            const {enabled} = repository[url]

            if (enabled) {
              accumulator.push(repository)
            }
          } else {
            accumulator.push(repository)
          }

          return accumulator
        },
        [],
      )
    } else {
      job.repositories.push({[url]: settings})
    }

    if (!job.repositories?.length) {
      deleteIfEmpty(job, 'repositories')
    }
  }
}
